import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { TokenTypeEnum } from 'authentication/enums/token-type.enum';

const API_URL = environment.iceDashboardUrl;

export interface WidgetDataState<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
}

@Injectable()
export class VisualsDashboardDataService<T = unknown> {
    private readonly state = signal<WidgetDataState<T>>({
        data: null,
        loading: false,
        error: null,
    });

    readonly data = computed(() => this.state().data);
    readonly loading = computed(() => this.state().loading);
    readonly error = computed(() => this.state().error);

    constructor(
        private http: HttpClient,
        private destroyRef: DestroyRef,
    ) {}

    load(widgetId: number, historyOption?: number): void {
        this.state.update((current) => ({ ...current, loading: true, error: null }));

        const params: Record<string, string> = { widgetId: widgetId.toString() };

        if (historyOption !== undefined) {
            params['historyOption'] = historyOption.toString();
        }

        this.http
            .get<T>(`${API_URL}/widgets/data`, {
                params,
                headers: { 'X-Token-Type': TokenTypeEnum.ICE_DASHBOARD },
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => this.state.set({ data: response, loading: false, error: null }),
                error: (err) => this.state.set({ data: null, loading: false, error: err }),
            });
    }

    refresh(widgetId: number, historyOption?: number): void {
        this.load(widgetId, historyOption);
    }
}
